// Pure derivations turning one `/fetch-coach-client-insights` record into the
// booleans the roster chips and row icons read. No React, no dates from the
// ambient clock — `now` is always injected so every branch is testable.

/** Windows the product fixed; the staleness window is the only tunable one. */
export const TRACKING_WINDOW_DAYS = 7;
export const NEW_CLIENT_WINDOW_DAYS = 7;

/**
 * Staleness windows offered in the Filters panel ("Stale after"). Three options,
 * defaulting to 7, per the Messages Bottom Sheet design. `insightPrefs.normalize`
 * falls back for anything outside this set, so a stored 10 from an earlier build
 * self-heals to the default rather than rendering a dead segment.
 */
export const THRESHOLD_OPTIONS = [7, 14, 30];
export const DEFAULT_THRESHOLD_DAYS = 7;

const MS_PER_DAY = 86400000;

/**
 * Midnight-normalised UTC epoch for a `YYYY-MM-DD` string or a Date.
 * Normalising both ends is what keeps `daysSince` from sliding by one as the
 * day wears on — a plan assigned 10 days ago must read as 10 all day, not flip
 * to 11 after lunch.
 *
 * @param {string|Date|null|undefined} value
 * @returns {number|null} Epoch ms at UTC midnight, or null if unparseable.
 */
function toUtcMidnight(value) {
  if (!value) return null;

  if (value instanceof Date) {
    const time = value.getTime();
    if (Number.isNaN(time)) return null;
    return Date.UTC(
      value.getUTCFullYear(),
      value.getUTCMonth(),
      value.getUTCDate()
    );
  }

  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value).trim());
  if (!match) return null;

  const [, year, month, day] = match;
  return Date.UTC(Number(year), Number(month) - 1, Number(day));
}

/**
 * Whole days between a date and `now`, both floored to UTC midnight.
 *
 * @param {string|Date|null|undefined} value
 * @param {Date} [now]
 * @returns {number|null} Null when the date is missing or unparseable.
 */
export function daysSince(value, now = new Date()) {
  const then = toUtcMidnight(value);
  const today = toUtcMidnight(now);
  if (then === null || today === null) return null;
  return Math.round((today - then) / MS_PER_DAY);
}

/**
 * Section status. The API reports each section as `"ok" | "none" | "error"`;
 * a missing field is read as `"ok"` so the endpoint can add the statuses after
 * the frontend ships without the two going out of step.
 *
 * `"error"` means the upstream diet/workout/subscription call failed for this
 * client. It is deliberately NOT collapsed into `"none"`: a diet-service
 * hiccup would otherwise tag every client "no diet plan", which is worse than
 * showing nothing.
 *
 * @param {string|undefined} status
 * @returns {boolean} True when the section failed and should read as unknown.
 */
const isUnknown = (status) => status === "error";

/** True when the section is present and usable. */
const isNone = (status) => status === "none";

/** Non-negative integer, defaulting to 0 — the API may send null or a string. */
const toCount = (value) => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
};

/**
 * The inert tag set for a client the insights payload does not cover — a room
 * that exists in comms but has no coaching record yet, or any row at all while
 * the feature is flagged off. Every predicate is false, so such a row is never
 * pulled into a filter and never renders an icon. Frozen because it is shared
 * by every untagged row rather than rebuilt per call.
 */
export const EMPTY_TAGS = Object.freeze({
  hasData: false,
  dietAgeDays: null,
  workoutAgeDays: null,
  dietStale: false,
  workoutStale: false,
  noDietPlan: false,
  noWorkoutPlan: false,
  noPlan: false,
  // Every section reads as unknown, so a row whose insights have not resolved
  // never makes a chip look available.
  dietUnknown: true,
  workoutUnknown: true,
  planUnknown: true,
  trackingUnknown: true,
  attachmentUnknown: true,
  workoutDateApproximate: false,
  foodLogs: 0,
  workoutLogs: 0,
  isTracking: false,
  notTracking: false,
  attachedDaysAgo: null,
  isNew: false,
  staleness: -1,
});

/**
 * Derives every roster tag for one client.
 *
 * Two rules worth stating, because they are the ones that surprise:
 * - "Stale" is strictly `age > threshold`. A plan assigned exactly N days ago
 *   is not yet overdue, matching how "over 10 days" reads.
 * - A plan that was never assigned is never "stale". It is `noPlan`, a separate
 *   chip, because "write them a fresh plan" and "they have never had one" are
 *   different jobs.
 *
 * @param {Object|null|undefined} insight - One `clients[]` record from the API.
 * @param {number} thresholdDays - Staleness window (7 | 10 | 14 | 30).
 * @param {Date} [now]
 * @returns {typeof EMPTY_TAGS}
 */
export function deriveTags(insight, thresholdDays = DEFAULT_THRESHOLD_DAYS, now = new Date()) {
  if (!insight) return EMPTY_TAGS;

  const dietUnknown = isUnknown(insight.diet_plan_status);
  const workoutUnknown = isUnknown(insight.workout_plan_status);
  const trackingUnknown = isUnknown(insight.tracking_status);
  const attachmentUnknown = isUnknown(insight.attachment_status);

  const dietAgeDays = dietUnknown ? null : daysSince(insight.diet_plan_assigned_on, now);
  const workoutAgeDays = workoutUnknown
    ? null
    : daysSince(insight.workout_plan_assigned_on, now);

  // "Never assigned" needs an age we could not find AND a section that actually
  // reported. An errored section knows nothing either way.
  const noDietPlan =
    !dietUnknown && (dietAgeDays === null || isNone(insight.diet_plan_status));
  const noWorkoutPlan =
    !workoutUnknown && (workoutAgeDays === null || isNone(insight.workout_plan_status));
  const noPlan = noDietPlan && noWorkoutPlan;

  const foodLogs = trackingUnknown ? 0 : toCount(insight.food_log_count);
  const workoutLogs = trackingUnknown ? 0 : toCount(insight.workout_log_count);
  const isTracking = !trackingUnknown && (foodLogs > 0 || workoutLogs > 0);

  const attachedDaysAgo = attachmentUnknown
    ? null
    : daysSince(insight.coach_attached_on, now);

  // Sort key for "most neglected first". Never-assigned has no age to compare,
  // but it is the most urgent case, so it takes a rank above any real age.
  const oldestPlanAge = Math.max(
    dietAgeDays === null ? -1 : dietAgeDays,
    workoutAgeDays === null ? -1 : workoutAgeDays
  );

  return {
    hasData: true,
    dietAgeDays,
    workoutAgeDays,
    dietStale: dietAgeDays !== null && dietAgeDays > thresholdDays,
    workoutStale: workoutAgeDays !== null && workoutAgeDays > thresholdDays,
    noDietPlan,
    noWorkoutPlan,
    noPlan,
    dietUnknown,
    workoutUnknown,
    // "No plan" needs BOTH sections to have reported, so one unknown makes the
    // answer unknown rather than false.
    planUnknown: dietUnknown || workoutUnknown,
    trackingUnknown,
    attachmentUnknown,
    workoutDateApproximate: Boolean(insight.workout_plan_assigned_is_approximate),
    foodLogs,
    workoutLogs,
    isTracking,
    // Not "!isTracking": a client whose tracking lookup failed is neither, and
    // must not be swept into the "chase these people" chip.
    notTracking: !trackingUnknown && !isTracking,
    attachedDaysAgo,
    isNew: attachedDaysAgo !== null && attachedDaysAgo <= NEW_CLIENT_WINDOW_DAYS,
    staleness: noPlan ? Number.POSITIVE_INFINITY : oldestPlanAge,
  };
}
